<template>
  <div
    v-tooltip="tooltipContent"
    class="rounded-full border flex items-center space-x-1.5 px-1.5 py-0.5 font-semibold text-label whitespace-nowrap select-none"
    data-cy="funder-placement-product-tag"
  >
    <div class="transform translate-y-0-25">
      <icon-base
        :icon="statusIconData.icon"
        :class="statusIconData.className"
        :fill="statusIconData.fill"
        width="16"
        height="16"
        data-cy="funder-placement-product-tag-status-icon"
      />
    </div>

    <span data-cy="funder-placement-product-tag-type">
      {{ placement.product_type }}
    </span>

    <span>&bull;</span>

    <span
      :class="{ 'text-additional-error': isDeclined }"
      data-cy="funder-placement-product-tag-time"
    >
      {{ displayedInfo }}
    </span>

    <div class="transform translate-y-0-25 text-label">
      <icon-base
        :icon="submissionIconData"
        width="16"
        height="16"
        data-cy="funder-placement-product-tag-submission-icon"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { type Component, computed } from "vue";
import { useStore } from "vuex";

import { formatTimeAgo } from "@/helpers/formatting";
import { getPlacementErrorMessage } from "@/helpers/placements";
import { PlacementStatuses } from "@/models/funders";

import IconApi from "@/components/icons/IconApi.vue";
import IconClock from "@/components/icons/IconClock.vue";
import IconCursorPointer from "@/components/icons/IconCursorPointer.vue";
import IconEmailOutline from "@/components/icons/IconEmailOutline.vue";
import IconExclamationMarkRound from "@/components/icons/IconExclamationMarkRound.vue";
import IconLendflowSubmission from "@/components/icons/IconLendflowSubmission.vue";
import IconSuccessRoundBig from "@/components/icons/IconSuccessRoundBig.vue";
import IconXCircle from "@/components/icons/IconXCircle.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { IPlacementTableRow } from "@/models/offers";
import type { SubmissionMethod } from "@/models/options";

const props = defineProps<{
  placement: IPlacementTableRow;
}>();

const store = useStore();

const placementStatuses = computed<Record<string, string>>(
  () => store.getters["options/all"]?.placement?.statuses ?? {}
);

const declineReasons = computed<Record<string, string>>(
  () => store.getters["options/placementAllDeclineReasons"] ?? {}
);

const placementStatusToIconDataMap: Record<
  PlacementStatuses,
  { icon: Component; className: string; fill?: string }
> = {
  [PlacementStatuses.Approved]: {
    icon: IconSuccessRoundBig,
    className: "text-green-500",
  },
  [PlacementStatuses.Declined]: {
    icon: IconXCircle,
    className: "text-red-500",
  },
  [PlacementStatuses.Pending]: {
    icon: IconClock,
    className: "text-orange-500",
  },
  [PlacementStatuses.Duplicate]: {
    icon: IconXCircle,
    className: "text-red-500",
  },
  [PlacementStatuses.Error]: {
    icon: IconExclamationMarkRound,
    className: "",
    fill: "#DC2626",
  },
  [PlacementStatuses.PendingMissingInfoOrDocs]: {
    icon: IconClock,
    className: "text-orange-500",
  },
  [PlacementStatuses.PendingReview]: {
    icon: IconClock,
    className: "text-orange-500",
  },
  [PlacementStatuses.Queued]: {
    icon: IconClock,
    className: "text-orange-500",
  },
};

const placementSubmissionIdToIconDataMap: Record<SubmissionMethod, Component> =
  {
    1: IconApi,
    2: IconEmailOutline,
    3: IconCursorPointer,
    4: IconLendflowSubmission,
  };

const isDeclined = computed(() => {
  return props.placement.status === PlacementStatuses.Declined;
});

const statusIconData = computed(() => {
  return placementStatusToIconDataMap[props.placement.status];
});

const declineReasonLabel = computed(() =>
  props.placement.decline_reason_id
    ? (declineReasons.value[props.placement.decline_reason_id] ?? "")
    : ""
);

const submissionIconData = computed(
  () => placementSubmissionIdToIconDataMap[props.placement.submissionId]
);

const submittedAt = computed(() =>
  formatTimeAgo(props.placement.created_at, true)
);

const displayedInfo = computed(() =>
  isDeclined.value ? declineReasonLabel.value : submittedAt.value
);

const statusLabel = computed(
  () => placementStatuses.value[props.placement.status]
);

const tooltipContent = computed(() => {
  if (props.placement.status !== PlacementStatuses.Error) {
    return statusLabel.value;
  }

  return getPlacementErrorMessage(props.placement) ?? statusLabel.value;
});
</script>
